import { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ICellRendererParams } from '@ag-grid-community/core'
import { fetchFreshCategories } from 'utils/api/freshCategoryApi'
import { useQuery } from 'hooks'
import useModal from 'hooks/useModal'
import { DataTable, DataTablePagination } from 'components/common'
import { getColumns } from './utils'
import ConfirmDeleteFreshCategoryModal from './confirmDeleteFreshCategoryModal'

type FreshCategoryTableProps = {
  isEditor: boolean
}

const FreshCategoryTable = ({ isEditor }: FreshCategoryTableProps) => {
  const [categoriesData, setCategoriesData] = useState<any>({})
  const [loading, setLoading] = useState<boolean>(false)
  const [gridParams, setGridParams] = useState<ICellRendererParams>()

  const { search } = useLocation()
  const { getAllQueries } = useQuery()
  const { closeModal, isOpen, openModal } = useModal()

  const getCategories = async () => {
    setLoading(true)
    const { limit, search: searchTerm, page, isActive } = getAllQueries()
    try {
      const { data } = await fetchFreshCategories({
        limit: limit || 10,
        page: page || 1,
        search: searchTerm,
        // getAllQueries JSON-parses values, so 'true' arrives as a boolean
        isActive: isActive === undefined ? undefined : String(isActive),
      })
      setCategoriesData({ ...data })
    } catch {
      setCategoriesData({ docs: [] })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getCategories()
  }, [search])

  const getCategoryToDelete = (params: ICellRendererParams) => {
    if (!isEditor) return
    setGridParams(params)
    openModal()
  }

  const columns = useMemo(
    () => getColumns(isEditor, getCategoryToDelete),
    [isEditor],
  )

  return (
    <>
      <DataTable
        columns={columns}
        rows={loading ? null : categoriesData?.docs}
        identifier="_id"
      />

      {isEditor && (
        <ConfirmDeleteFreshCategoryModal
          params={gridParams}
          closeModal={closeModal}
          isOpen={isOpen}
        />
      )}

      <DataTablePagination
        currentPage={categoriesData?.page}
        totalPages={categoriesData?.totalPages}
        hasNextPage={categoriesData?.hasNextPage}
        hasPrevPage={categoriesData?.hasPrevPage}
        limit={categoriesData?.limit}
        nextPage={categoriesData?.nextPage}
        prevPage={categoriesData?.prevPage}
        disabled={loading}
      />
    </>
  )
}

export default FreshCategoryTable
